/**
 * ICP Tests List Page - View and filter ICP test results
 */
import { useMemo, useState } from 'react';
import {
  Container,
  Title,
  Stack,
  Paper,
  Alert,
  Table,
  ScrollArea,
  Text,
  Badge,
  Group,
  Loader,
  Center,
  MultiSelect,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertCircle } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useICPTests } from '@/hooks/useICPTests';
import { ICPImportForm } from '@/components/ICPImportForm';
import { ProviderBadge } from '@/components/ICP/ProviderBadge';
import { PROVIDER_LABELS } from '@/utils/icpProviderDetect';
import type { ICPProvider } from '@/types/sensor';

// Format date for display - stacked on mobile, single line on desktop
// Moved to module level to avoid re-creation on every render
function formatDate(dateStr: string | undefined, mobile: boolean): { line1: string; line2: string | null } {
  if (!dateStr) return { line1: 'N/A', line2: null };
  const date = new Date(dateStr);
  if (mobile) {
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return { line1: `${day} ${month}`, line2: `${year}` };
  }
  return { line1: date.toLocaleDateString(), line2: null };
}

export default function ICPTestsPage() {
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Provider filter selection (MultiSelect). Empty = show all providers.
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);

  // When exactly one provider is selected, push it to the server query (the
  // list endpoint takes a single `provider` param). For 0 or multiple
  // selections we fetch all and filter client-side below.
  const serverProvider =
    selectedProviders.length === 1 ? (selectedProviders[0] as ICPProvider) : undefined;

  // Fetch first 100 tests with React Query
  // See task-19 (pagination) and task-20 (sorting) for planned improvements
  const { data: response, isLoading, error } = useICPTests({
    limit: 100,
    provider: serverProvider,
  });

  const allTests = useMemo(() => response?.tests ?? [], [response]);

  // Distinct providers present in the loaded data. The provider filter is only
  // meaningful (and only shown) when the loaded tests span ≥2 providers.
  const distinctProviders = useMemo(() => {
    const set = new Set<string>();
    for (const t of allTests) {
      if (t.provider) set.add(t.provider);
    }
    return set;
  }, [allTests]);

  // Show the filter when the loaded tests span multiple providers, or while a
  // filter is already active (so it can be cleared even if it narrowed to one).
  const showProviderFilter = distinctProviders.size >= 2 || selectedProviders.length > 0;

  // Client-side filtering covers the multi-select case (and is a no-op when the
  // server already filtered to a single provider).
  const visibleTests = useMemo(() => {
    if (selectedProviders.length === 0) return allTests;
    return allTests.filter((t) => selectedProviders.includes(t.provider));
  }, [allTests, selectedProviders]);

  const providerOptions = useMemo(() => {
    // Offer every provider seen in the data plus any currently selected.
    const ids = new Set<string>([...distinctProviders, ...selectedProviders]);
    return Array.from(ids).map((id) => ({
      value: id,
      label: (PROVIDER_LABELS as Record<string, string>)[id] ?? id,
    }));
  }, [distinctProviders, selectedProviders]);

  // Calculate whether to show tank column - only if user has multiple tanks
  const uniqueTanks = new Set(visibleTests.map((t) => t.tank_name));
  const showTankColumn = uniqueTanks.size > 1;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Title order={1}>ICP Tests</Title>

        {/* Import Form */}
        <ICPImportForm />

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error loading ICP tests"
            color="red"
          >
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </Alert>
        )}

        <Paper p="md" withBorder>
          <Stack gap="md">
            {/* Filters + results info */}
            <Group justify="space-between" align="flex-end" wrap="wrap">
              {response && (
                <Text size="sm" c="dimmed">
                  Showing {visibleTests.length} test{visibleTests.length !== 1 ? 's' : ''}
                </Text>
              )}
              {showProviderFilter && (
                <MultiSelect
                  label="Provider"
                  placeholder={selectedProviders.length === 0 ? 'All providers' : undefined}
                  data={providerOptions}
                  value={selectedProviders}
                  onChange={setSelectedProviders}
                  clearable
                  size="sm"
                  w={{ base: '100%', sm: 280 }}
                  aria-label="Filter by ICP provider"
                />
              )}
            </Group>

            {/* Data Table */}
            {isLoading ? (
              <Center py="xl">
                <Loader size="lg" />
              </Center>
            ) : (
              <ScrollArea>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Date</Table.Th>
                      {!isMobile && <Table.Th>Barcode</Table.Th>}
                      {showTankColumn && <Table.Th>Tank</Table.Th>}
                      <Table.Th>Key Parameters</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {visibleTests.length > 0 ? (
                      visibleTests.map((test) => (
                        <Table.Tr
                          key={test.barcode}
                          style={{ cursor: 'pointer' }}
                        >
                          <Link
                            to={`/icp-tests/${test.barcode}`}
                            style={{ display: 'contents', color: 'inherit', textDecoration: 'none' }}
                          >
                            <Table.Td>
                              {(() => {
                                const formatted = formatDate(test.test_date, isMobile ?? false);
                                return (
                                  <Stack gap={2}>
                                    <Text size="sm">{formatted.line1}</Text>
                                    {formatted.line2 && (
                                      <Text size="xs" c="dimmed">{formatted.line2}</Text>
                                    )}
                                    {/* Mobile has no barcode column - show provider here */}
                                    {isMobile && (
                                      <ProviderBadge provider={test.provider} size="xs" />
                                    )}
                                  </Stack>
                                );
                              })()}
                            </Table.Td>
                            {!isMobile && (
                              <Table.Td>
                                <Stack gap={2} align="flex-start">
                                  <Text size="sm" fw={500}>
                                    {test.barcode}
                                  </Text>
                                  <ProviderBadge provider={test.provider} size="xs" />
                                </Stack>
                              </Table.Td>
                            )}
                            {showTankColumn && (
                              <Table.Td>
                                <Text size="sm" c="dimmed">
                                  {test.tank_name || 'Unknown'}
                                </Text>
                              </Table.Td>
                            )}
                            <Table.Td>
                              <Group gap="xs">
                                {test.calcium !== undefined && test.calcium !== null && (
                                  <Badge variant="light" color="blue">
                                    Ca: {test.calcium.toFixed(0)}
                                  </Badge>
                                )}
                                {test.alkalinity !== undefined &&
                                  test.alkalinity !== null && (
                                    <Badge variant="light" color="green">
                                      Alk: {test.alkalinity.toFixed(1)}
                                    </Badge>
                                  )}
                                {test.magnesium !== undefined &&
                                  test.magnesium !== null && (
                                    <Badge variant="light" color="grape">
                                      Mg: {test.magnesium.toFixed(0)}
                                    </Badge>
                                  )}
                              </Group>
                            </Table.Td>
                          </Link>
                        </Table.Tr>
                      ))
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={2 + (isMobile ? 0 : 1) + (showTankColumn ? 1 : 0)}>
                          <Center py="xl">
                            <Text c="dimmed">No ICP tests found</Text>
                          </Center>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
